// library-doc.service.ts

import { computed, inject, Injectable } from '@angular/core';
import { CodeLibraries, DocClassEntry, DocClassEntryWithTypes, DocLibraries, ShowcaseCodeEntry } from '../types/component-docs.model';
import { DocMeta } from "../decorators";
import { EzBaseService } from "@template-ui/services/ez-base-service";
import { APP_SHOWCASE_CODE_LIBRARIES_TOKEN, APP_SHOWCASE_DOC_LIBRARIES_TOKEN } from "@template-ui/tokens";
import { EzShowcaseCodeLibrariesExtension, EzShowcaseDocLibrariesExtension } from "@template-ui/types";
//import { TypedocIndexService } from "../services/type-doc-index.service";




/**
 * # Library Doc Service
 * How to add content to the showcase.
 *
 * ## Add an template-ui sample (library-owned sample)
 * 1. Create a sample component and attach it with `showcaseAttachTo`.
 * ```ts
 * @DocMeta({
 *   hide: true,
 *   showcaseTitle: "Basic Example",
 *   showcaseDesc: "Shows the default behavior.",
 *   showcaseAttachTo: [{ libName: SHOWCASE_LIBRARY_NAMES.BASE, classNames: ["EzWidgetComponent"] }]
 * })
 * export class EzWidgetBasicSampleComponent {}
 * ```
 * 2. Run `npm run docs`.
 * 3. Confirm the sample appears on the target EZ class page in **Examples**.
 *
 * ## Attach an app-level sample to an EZ page (without changing template-ui docs)
 * Sample tiles will have a different color to distinguish from internal EZ Library samples
 * 1. Create the app sample component and attach it by setting `showcaseAttachTo` in `@DocMeta`.
 * ```ts
 * @DocMeta({
 *   hide: true,
 *   showcaseTitle: "App Integration Example",
 *   showcaseDesc: "Shows app-specific usage.",
 *   showcaseAttachTo: [{ libName: SHOWCASE_LIBRARY_NAMES.BASE, classNames: ["EzWidgetComponent"] }]
 * })
 * export class AppWidgetSampleComponent {}
 * ```
 * 2. Run `npm run docs`.
 * 3. Confirm the sample appears on the target EZ class page in **Examples**.
 *
 *
 * ## Attach an app-level sample to an app-level component page
 * 1. In the app sample component, use `showcaseAttachTo` with the app library name and target app class.
 * ```ts
 * @DocMeta({
 *   hide: true,
 *   showcaseTitle: "Advanced App Example",
 *   showcaseDesc: "Shows app-specific integration.",
 *   showcaseAttachTo: [{ libName: SHOWCASE_LIBRARY_NAMES.APP, classNames: ["ActivityLogComponent"] }]
 * })
 * export class ActivityLogAdvancedSampleComponent {}
 * ```
 * 2. Run `npm run docs`.
 * 3. Confirm the sample appears on the target app component page in **Examples**.
 *
 * ## Two attachment methods (and the standard going forward)
 * - `showcaseComponentNames`: target component lists sample class names (deprecated)
 * - `showcaseAttachTo`: sample component declares its target component(s).
 * - Both methods still work.
 * - Standard going forward: use `showcaseAttachTo` for all new samples (same-library and cross-library).
 *
 * ## Quick checklist when it does not show up
 * - Use constants where available (for example, `SHOWCASE_LIBRARY_NAMES.BASE` and `SHOWCASE_LIBRARY_NAMES.APP`) instead of hard-coded library names.
 * - `libName` exactly matches the target library key.
 * - `classNames` entries exactly match documented class names.
 * - Docs were regenerated after metadata changes.
 */
@DocMeta({
  title: "Library Doc Service",
  category: "Services"
})
@Injectable({ providedIn: 'root' })
export class LibraryDocService extends EzBaseService {

  private readonly docBase = "assets/template-ui/docs/"
  private readonly appDocBase = "assets/"

  private readonly baseLibDocsSig = this.crudService.objectFromJsonFileSig<DocLibraries>(`${this.docBase}LibraryDocs.json`);
  private readonly baseCodeDocsSig = this.crudService.objectFromJsonFileSig<CodeLibraries>(`${this.docBase}LibrarySamples.json`);
  private readonly appLibDocsSig = this.crudService.objectFromJsonFileSig<DocLibraries>(`${this.appDocBase}LibraryDocs.json`);
  private readonly appCodeDocsSig = this.crudService.objectFromJsonFileSig<CodeLibraries>(`${this.appDocBase}LibrarySamples.json`);

  private readonly appDocLibraryExtensions =
    inject(APP_SHOWCASE_DOC_LIBRARIES_TOKEN, { optional: true }) as EzShowcaseDocLibrariesExtension[] | null;

  private readonly appCodeLibraryExtensions =
    inject(APP_SHOWCASE_CODE_LIBRARIES_TOKEN, { optional: true }) as EzShowcaseCodeLibrariesExtension[] | null;

  /**
   * Merged doc libraries used by the showcase.
   * Base template-ui docs are merged with optional app-provided extension libraries.
   * Extension entries are applied last (last-wins on duplicate keys).
   */
  readonly libDocsSig = computed<DocLibraries>(() => {
    const base = this.baseLibDocsSig() ?? {};
    const app = this.appLibDocsSig() ?? {};
    const merged = this.mergeDocLibraries(this.mergeDocLibraryMaps(base, app), this.appDocLibraryExtensions);
    return this.applyShowcaseSampleAttachments(merged);
  });

  /**
   * Merged sample code libraries used by the showcase.
   * Base template-ui sample metadata is merged with optional app-provided extension libraries.
   * Extension entries are applied last (last-wins on duplicate keys).
   */
  readonly codeDocsSig = computed<CodeLibraries>(() => {
    const base = this.baseCodeDocsSig() ?? {};
    const app = this.appCodeDocsSig() ?? {};
    return this.mergeCodeLibraries(this.mergeCodeLibraryMaps(base, app), this.appCodeLibraryExtensions);
  });

  // Temp removed cuz TypedocIndexService has external dependencies.
  //private typeDoc = inject(TypedocIndexService);
  // constructor() {
  //   //this.mapLibs = Object.keys(libraryDocsJson)
  // }

  getDocFor(libName: string, className: string): DocClassEntryWithTypes | undefined {
    const entry = this._getDocFor(libName, className); // your existing lookup
    if (!entry) return;
    //console.log('Found doc entry for', className, entry);

    if (!entry?.relatedTypeFiles?.length) {
      // ENFORCE the standard: fail fast in dev / fail CI
      // if (!environment.production) {
      //   throw new Error(
      //     `[Docs] ${className} is missing @docMeta({ relatedTypesFromFiles: [...] }).`
      //   );
      // }
      // In prod, show a friendly note
      return {
        ...entry,
        relatedTypeFiles: [],
        relatedTypesDocs:
          '>Related types are not configured. Add `@docMeta({ relatedTypesFromFiles: [...] })`.'
      };
    }

    // If you preload TypeDoc (APP_INITIALIZER), use the sync version:
    // Temp removed cuz TypedocIndexService has external dependencies.
    //const md = this.typeDoc.getTypesFromFilesMdSync(entry.relatedTypeFiles ?? []);

    return {
      ...entry,
      relatedTypeFiles: entry.relatedTypeFiles,
      //relatedTypesDocs: md
    };
  }

  _getDocFor(libName: string, className: string): DocClassEntry | undefined {


    if (this.libDocsSig() && (this.libDocsSig() as any)[libName]) {
      const lib = (this.libDocsSig() as any)[libName];

      if (lib && (lib as any)[className])
        return (lib as any)[className];

    }

    return undefined;
  }

  getCodeFor(libName: string, className: string): ShowcaseCodeEntry | undefined {
    const codeDocs = this.codeDocsSig();
    if (codeDocs && (codeDocs as any)[libName]) {
      const lib = (codeDocs as any)[libName];
      if (lib && (lib as any)[className]) {
        return (lib as any)[className];
      }
    }

    // Fallback: find the sample in any loaded library so base-library docs
    // can render app-hosted sample code without manual extension wiring.
    if (codeDocs) {
      for (const libraryName of Object.keys(codeDocs)) {
        const libraryEntries = (codeDocs as any)[libraryName];
        if (libraryEntries?.[className]) {
          return libraryEntries[className];
        }
      }
    }

    return undefined;
  }

  /**
   * Returns true when a sample's code entry exists in the specified library without cross-library fallback.
   * This is used by the showcase UI to distinguish library-owned samples from externally attached samples.
   */
  hasCodeForInLibrary(libName: string, className: string): boolean {
    const codeDocs = this.codeDocsSig();
    const libEntries = codeDocs?.[libName];
    return !!libEntries?.[className];
  }


  /**
   * Get all registered class names
   */
  getAllLibClassNames(libKey: string) {

    const rtn: string[] = [];

    const libDocs = this.libDocsSig();

    if(libDocs)
    {
      Object.keys(libDocs).forEach((lib: string) => {
        rtn.push(...Object.keys((libDocs)[lib]));
      });
    }

    return rtn;
  }

  private mergeDocLibraries(
    base: DocLibraries,
    extensions: EzShowcaseDocLibrariesExtension[] | null
  ): DocLibraries {
    const merged: DocLibraries = { ...base };

    if (!extensions?.length) {
      return merged;
    }

    for (const extension of extensions) {
      const extensionLibraries = extension?.libraries;
      if (!extensionLibraries) {
        continue;
      }

      for (const [libraryName, docEntries] of Object.entries(extensionLibraries)) {
        merged[libraryName] = {
          ...(merged[libraryName] ?? {}),
          ...(docEntries ?? {}),
        };
      }
    }

    return merged;
  }

  private mergeDocLibraryMaps(base: DocLibraries, additions: DocLibraries): DocLibraries {
    const merged: DocLibraries = { ...base };

    for (const [libraryName, docEntries] of Object.entries(additions ?? {})) {
      merged[libraryName] = {
        ...(merged[libraryName] ?? {}),
        ...(docEntries ?? {}),
      };
    }

    return merged;
  }

  private applyShowcaseSampleAttachments(libraries: DocLibraries): DocLibraries {
    const merged: DocLibraries = { ...libraries };

    for (const [sourceLibName, docEntries] of Object.entries(libraries)) {
      for (const [sampleClassName, sampleDoc] of Object.entries(docEntries ?? {})) {
        const attachTargets = sampleDoc?.docMeta?.showcaseAttachTo;
        if (!attachTargets?.length) {
          continue;
        }

        for (const target of attachTargets) {
          const targetLibName = target?.libName;
          const targetClassNames = target?.classNames?.length
            ? target.classNames
            : target?.className
              ? [target.className]
              : [];

          if (!targetLibName || targetClassNames.length < 1) {
            continue;
          }

          for (const targetClassName of targetClassNames) {
            const targetLibraryEntries = merged[targetLibName];
            const targetDoc = targetLibraryEntries?.[targetClassName];
            if (!targetDoc) {
              continue;
            }

            const existingSampleNames = targetDoc.showcaseComponentNames ?? [];
            if (!existingSampleNames.includes(sampleClassName)) {
              targetDoc.showcaseComponentNames = [...existingSampleNames, sampleClassName];
            }

            if (targetDoc.docMeta) {
              const existingDocMetaSampleNames = targetDoc.docMeta.showcaseComponentNames ?? [];
              if (!existingDocMetaSampleNames.includes(sampleClassName)) {
                targetDoc.docMeta.showcaseComponentNames = [...existingDocMetaSampleNames, sampleClassName];
              }
            }

            if (!merged[targetLibName]) {
              merged[targetLibName] = {};
            }
            merged[targetLibName][targetClassName] = targetDoc;
          }
        }
      }
    }

    return merged;
  }

  private mergeCodeLibraries(
    base: CodeLibraries,
    extensions: EzShowcaseCodeLibrariesExtension[] | null
  ): CodeLibraries {
    const merged: CodeLibraries = { ...base };

    if (!extensions?.length) {
      return merged;
    }

    for (const extension of extensions) {
      const extensionLibraries = extension?.libraries;
      if (!extensionLibraries) {
        continue;
      }

      for (const [libraryName, codeEntries] of Object.entries(extensionLibraries)) {
        merged[libraryName] = {
          ...(merged[libraryName] ?? {}),
          ...(codeEntries ?? {}),
        };
      }
    }

    return merged;
  }

  private mergeCodeLibraryMaps(base: CodeLibraries, additions: CodeLibraries): CodeLibraries {
    const merged: CodeLibraries = { ...base };

    for (const [libraryName, codeEntries] of Object.entries(additions ?? {})) {
      merged[libraryName] = {
        ...(merged[libraryName] ?? {}),
        ...(codeEntries ?? {}),
      };
    }

    return merged;
  }
}
